import { useTranslation } from "react-i18next";
import { Badge, type BadgeVariant } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Tooltip } from "@/components/Tooltip";
import type { TableColumn } from "@/components/Table";
import type { Tender } from "@/pages/Tenders/types";
import { price, percentage } from "@/utils/format";

export const useStudyColumns = (
    handleOpenStatus: (study: Tender) => void,
    setSelectedStudy: (study: Tender) => void
): TableColumn<Tender>[] => {
    const { t } = useTranslation("global");
    return [
        {
            key: "submission_date",
            label: t("tender.col_submission_date"),
            type: "date",
            sortable: true,
            align: "center",
            nowrap: true,
        },
        {
            key: "title",
            label: t("tender.col_work"),
            sortable: true,
            render: (study: Tender) => (
                <div className="flex flex-col py-0.5 min-w-[220px]">
                    <Button
                        variant="ghost"
                        onClick={() => setSelectedStudy(study)}
                        className="!justify-start !p-0 !text-left !text-sm !font-semibold !text-primary dark:!text-primary-300 hover:!underline"
                    >
                        <span className="line-clamp-2" title={study.title}>
                            {study.title}
                        </span>
                    </Button>
                    <span className="text-[12px] text-slate-500 dark:text-zinc-400 line-clamp-1">
                        {study.city || ""}
                    </span>
                </div>
            ),
        },
        {
            key: "project_type_id",
            sortKey: "project_type.name",
            label: t("tender.col_work_type"),
            sortable: true,
            nowrap: true,
            render: (study: Tender) => {
                const pt = study.project_type || study.work_type;
                if (!pt) {
                    return <span className="text-slate-400"></span>;
                }
                const color = pt.color || "secondary";
                const icon = pt.icon || "hammer";
                return <Badge color={color} icon={icon} value={pt.name} />;
            },
        },
        {
            key: "client_id",
            sortKey: "client.name",
            label: t("tender.col_client"),
            sortable: true,
            render: (study: Tender) => {
                const clientName = study.client?.name;
                if (!clientName) {
                    return <span className="text-slate-400"></span>;
                }
                return (
                    <Tooltip title={clientName} placement="top">
                        <span className="flex flex-col text-slate-700 dark:text-zinc-300 line-clamp-1 max-w-[200px]">
                            {clientName}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            key: "client_type_id",
            sortKey: "client.client_type.name",
            label: t("tender.col_client_type"),
            sortable: true,
            nowrap: true,
            render: (study: Tender) => {
                const clientType =
                    study.client?.client_type || study.client_type;
                const clientTypeName = clientType?.name;
                if (!clientTypeName) {
                    return <span className="text-slate-400"></span>;
                }
                const isPublic =
                    clientTypeName.toLowerCase().includes("públic") ||
                    clientTypeName.toLowerCase().includes("public");
                const isCivil = clientTypeName.toLowerCase().includes("civil");
                const defaultFallback = isPublic
                    ? "success"
                    : isCivil
                      ? "neutral"
                      : "warning";
                const defaultIcon = isPublic
                    ? "landmark"
                    : isCivil
                      ? "hardHat"
                      : "house";
                const color = clientType?.color || defaultFallback;
                const icon = clientType?.icon || defaultIcon;
                return (
                    <Badge color={color} icon={icon} value={clientTypeName} />
                );
            },
        },
        {
            key: "budget",
            label: t("tender.col_budget"),
            type: "price",
            sortable: true,
            align: "right",
            nowrap: true,
        },
        {
            key: "bc3_percentage",
            label: t("studies.col_bc3_deviation"),
            sortable: true,
            align: "right",
            nowrap: true,
            render: (study: Tender) => {
                const pct = study.bc3_percentage ?? 0;
                const amount =
                    study.budget && pct ? (study.budget * pct) / 100 : 0;
                const formattedAmount = price(amount);
                //const formattedAmount = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
                return (
                    <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xs font-semibold text-slate-800 dark:text-zinc-200">
                            {formattedAmount}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-zinc-400">
                            {percentage(pct)}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "status",
            sortKey: "status_id",
            label: t("tender.col_status"),
            sortable: true,
            render: (study: Tender) => {
                const sId = study.status_id;
                const statusMap: Record<
                    number,
                    { labelKey: string; variant: BadgeVariant }
                > = {
                    5: { labelKey: "studies.status.in_study", variant: "info" },
                    6: {
                        labelKey: "studies.status.submitted",
                        variant: "primary",
                    },
                    7: {
                        labelKey: "studies.status.not_submitted",
                        variant: "danger",
                    },
                    8: {
                        labelKey: "studies.status.awarded",
                        variant: "success",
                    },
                    9: {
                        labelKey: "studies.status.not_awarded",
                        variant: "neutral",
                    },
                };
                const info = statusMap[sId] ?? {
                    labelKey: "studies.status.in_study",
                    variant: "neutral",
                };
                return (
                    <Badge
                        variant={info.variant}
                        value={t(info.labelKey)}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenStatus(study);
                        }}
                    />
                );
            },
        },
    ];
};
